import { imgSrcSet } from "../lib/media";
import React, { useState, useEffect } from "react";
import { Search, Users, Check, Clock, Trash2, Coins, Loader2, AlertTriangle, ExternalLink, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { CategoryBudget } from "../components/CategoryBudget";
import { api } from "../lib/api";
import { useConfig, symbolForCurrency, nf, formatMoney } from "../lib/format";
import { PageHeader, Loader } from "../components/shared";
import { Modal } from "../components/Modal";
import { MySaves } from "../components/MySaves";
import { SaveChangesBar } from "../components/SaveChangesBar";
import { PageTutorial } from "../components/PageTutorial";
import { TABLE_TUTORIAL } from "../tutorials/table";
import ProductResults from "../components/ProductResults";
import { ProductSearchBar } from "../components/ProductSearchBar";


export default function TableSetting() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const config = useConfig();
  const [items, setItems] = useState(null);
  const [budget, setBudget] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [active, setActive] = useState(null);
  const planGuests = Number(user?.onboarding?.guests) || 6;
  const [guests, setGuests] = useState(planGuests);
  const guestOptions = Array.from({ length: Math.max(planGuests, 12) }, (_, i) => i + 1);
  const [guide, setGuide] = useState(null);
  const [prodModal, setProdModal] = useState(null);
  const [selectedEls, setSelectedEls] = useState([]);
  const [saves, setSaves] = useState([]);
  const [draft, setDraft] = useState(null); // { saveId, slots, items } — unsaved edits of a snapshot

  const country = user?.country || "BR";
  const symbol = symbolForCurrency(config, user?.currency || "BRL");
  const fmt = (v) => formatMoney(symbol, v || 0);

  const buildItems = (tb, g) => [
    ...((tb?.items) || []).map((s, i) => ({ key: `b${i}`, label: s })),
    ...((g?.elements) || []).map((e) => ({ key: `e${e.id}`, label: `${e.count} ${e.label}` })),
  ];
  const toggleEl = (key) => setSelectedEls((s) => s.includes(key) ? s.filter((k) => k !== key) : [...s, key]);

  useEffect(() => {
    api.get(`/content/tablescapes?lang=${lang}`).then(({ data }) => setItems(data.tablescapes)).catch(() => {});
  }, [lang]);
  useEffect(() => {
    api.get("/table/saves").then(({ data }) => setSaves(data.saves || [])).catch(() => {});
    api.get("/budget").then(({ data }) => setBudget(data.budget?.categories?.table?.limit ?? null)).catch(() => {});
  }, []);
  useEffect(() => {
    // Price the chosen style on Google Shopping (user's market) whenever a style/guest count is picked.
    if (!active || !budget) { setPlan(null); return; }
    let alive = true;
    setPlanLoading(true); setPlan(null);
    api.get(`/products/table-plan?budget=${budget}&style=${encodeURIComponent(active.id)}&guests=${guests}&country=${country}`)
      .then(({ data }) => { if (alive) setPlan(data); })
      .catch(() => { if (alive) setPlan({ state: "error", slots: [] }); })
      .finally(() => { if (alive) setPlanLoading(false); });
    return () => { alive = false; };
  }, [active, guests, budget, country]);
  useEffect(() => {
    if (!active) return;
    api.get(`/content/table-guide?lang=${lang}&guests=${guests}`).then(({ data }) => {
      setGuide(data);
      setSelectedEls(buildItems(active, data).map((it) => it.key));
    }).catch(() => {});
  }, [active, guests, lang]);

  if (!items) return <Loader />;

  const patchSave = async (id, body) => {
    try { const { data } = await api.patch(`/table/saves/${id}`, body); setSaves(data.saves || []); toast.success(t("saves.updated")); }
    catch { toast.error("Error"); }
  };
  const findSavedItem = async (sv, it) => {
    const styleName = items.find((x) => x.id === sv.style)?.name || "";
    setProdModal({ label: it, loading: true, result: null });
    try {
      const { data: res } = await api.get(`/products/search?q=${encodeURIComponent(`${it} ${styleName} ${t("nav.table")}`)}&country=${country}&limit=12&min_results=8`);
      setProdModal({ label: it, loading: false, result: res });
    } catch { setProdModal({ label: it, loading: false, result: { state: "error" } }); }
  };
  const openSlotOptions = async (sl) => {
    setProdModal({ slotId: sl.id, label: `${sl.label} · ×${sl.quantity}`, loading: true, result: null });
    try {
      const maxP = Math.max(1, Math.round((sl.allocated / sl.quantity) * 1.3));
      const { data: res } = await api.get(`/products/search?q=${encodeURIComponent(sl.query)}&country=${country}&max_price=${maxP}&limit=12&min_results=8`);
      setProdModal((m) => ({ ...m, loading: false, result: res }));
    } catch { setProdModal((m) => ({ ...(m || {}), loading: false, result: { state: "error" } })); }
  };
  const openCustomSearch = async (q) => {
    const room = plan?.state === "ok" ? Math.max(0, Number(plan.remaining_estimate) || 0) : null;
    setProdModal({ custom: { id: `custom_${Date.now()}`, query: q }, label: q, loading: true, result: null });
    try {
      const maxP = room != null && room >= 1 ? `&max_price=${Math.round(room)}` : "";
      const { data: res } = await api.get(`/products/search?q=${encodeURIComponent(q)}&country=${country}${maxP}&limit=12&min_results=8&diverse=1`);
      setProdModal((m) => ({ ...m, loading: false, result: res }));
    } catch { setProdModal((m) => ({ ...(m || {}), loading: false, result: { state: "error" } })); }
  };
  const recalc = (slots) => {
    const spent = Math.round(slots.reduce((a, sl) => a + (sl.line_total || 0), 0) * 100) / 100;
    setPlan({ ...plan, slots, spent_estimate: spent, remaining_estimate: Math.round((plan.budget - spent) * 100) / 100 });
  };
  const setSlotQty = (id, qty) => recalc(plan.slots.map((sl) => sl.id === id
    ? { ...sl, quantity: qty, line_total: Math.round((sl.product?.extracted_price || 0) * qty * 100) / 100 } : sl));
  const removeSlot = (id) => recalc(plan.slots.filter((sl) => sl.id !== id));
  // Saved snapshot editing: changes go to a local draft until "Save changes".
  const viewOf = (sv) => (draft?.saveId === sv.save_id ? { ...sv, slots: draft.slots, items: draft.items, spent_estimate: draft.slots.length ? Math.round(draft.slots.reduce((a, sl) => a + Number(sl.line_total || 0), 0) * 100) / 100 : sv.spent_estimate } : sv);
  const editDraft = (sv, patch) => setDraft((d) => {
    const base = d?.saveId === sv.save_id ? d : { saveId: sv.save_id, slots: sv.slots || [], items: sv.items || [] };
    return { ...base, ...patch };
  });
  const setSavedQty = (sv, idx, qty) => editDraft(sv, { slots: (viewOf(sv).slots || []).map((sl, i) => i === idx
    ? { ...sl, quantity: qty, line_total: Math.round((sl.product?.extracted_price || (sl.line_total / (sl.quantity || 1)) || 0) * qty * 100) / 100 } : sl) });
  const removeSavedSlot = (sv, idx) => editDraft(sv, { slots: (viewOf(sv).slots || []).filter((_, i) => i !== idx) });
  const removeSavedItem = (sv, idx) => editDraft(sv, { items: (viewOf(sv).items || []).filter((_, i) => i !== idx) });
  const saveDraft = async () => {
    if (!draft) return;
    await patchSave(draft.saveId, { slots: draft.slots, items: draft.items });
    setDraft(null);
  };
  const openSavedSlotOptions = async (sv, idx) => {
    const cur = viewOf(sv);
    const sl = (cur.slots || [])[idx];
    setProdModal({ saved: { saveId: sv.save_id, idx }, label: `${sl.label} · ×${sl.quantity}`, loading: true, result: null });
    try {
      const unit = budget != null ? Math.max(1, Math.round(((budget - (Number(cur.spent_estimate || 0) - Number(sl.line_total || 0))) / (sl.quantity || 1)) * 1.2)) : null;
      const maxP = unit && unit > 0 ? `&max_price=${unit}` : "";
      const { data: res } = await api.get(`/products/table-slot-options?slot_id=${encodeURIComponent(sl.id)}&style=${encodeURIComponent(sv.style || "traditional")}&country=${country}${maxP}`);
      setProdModal((m) => ({ ...m, loading: false, result: res }));
    } catch { setProdModal((m) => ({ ...(m || {}), loading: false, result: { state: "error" } })); }
  };
  const chooseSlotProduct = (product) => {
    if (prodModal?.saved) {
      const sv = saves.find((x) => x.save_id === prodModal.saved.saveId);
      if (sv) {
        editDraft(sv, { slots: (viewOf(sv).slots || []).map((sl, i) => i === prodModal.saved.idx
          ? { ...sl, product, line_total: Math.round((product.extracted_price || 0) * (sl.quantity || 1) * 100) / 100 } : sl) });
      }
      setProdModal(null);
      return;
    }
    if (!plan || (!prodModal?.slotId && !prodModal?.custom)) return;
    const slots = prodModal.custom
      ? [...plan.slots, { id: prodModal.custom.id, label: prodModal.label, query: prodModal.custom.query, quantity: 1, allocated: Math.max(0, Number(plan.remaining_estimate) || 0), product, line_total: Math.round((product.extracted_price || 0) * 100) / 100 }]
      : plan.slots.map((sl) => sl.id === prodModal.slotId ? { ...sl, product, line_total: Math.round((product.extracted_price || 0) * sl.quantity * 100) / 100 } : sl);
    const spent = Math.round(slots.reduce((a, sl) => a + (sl.line_total || 0), 0) * 100) / 100;
    setPlan({ ...plan, slots, spent_estimate: spent, remaining_estimate: Math.round((plan.budget - spent) * 100) / 100 });
    setProdModal(null);
  };
  const findItems = async (tb) => {
    setProdModal({ label: tb.name, loading: true, result: null });
    try {
      const q = `${tb.name} ${t("nav.table")}`;
      const { data: res } = await api.get(`/products/search?q=${encodeURIComponent(q)}&country=${country}`);
      setProdModal({ label: tb.name, loading: false, result: res });
    } catch { setProdModal({ label: tb.name, loading: false, result: { state: "error" } }); }
  };
  const saveTable = async () => {
    if (saves.length >= 3) { toast.error(t("saves.full")); return; }
    const all = buildItems(active, guide).filter((it) => selectedEls.includes(it.key)).map((it) => it.label);
    if (!all.length) { toast.error(t("share.empty")); return; }
    try {
      const { data } = await api.post("/table/saves", { style: active.id, guests, items: all,
        spent_estimate: plan?.state === "ok" ? plan.spent_estimate : null,
        slots: plan?.state === "ok" ? plan.slots.filter((sl) => sl.product).map((sl) => ({ id: sl.id, label: sl.label, quantity: sl.quantity, line_total: sl.line_total, product: sl.product })) : [] });
      setSaves(data.saves || []);
      toast.success(t("saves.saved"));
    } catch { toast.error("Error"); }
  };
  const deleteSave = async (id) => {
    try { const { data } = await api.delete(`/table/saves/${id}`); setSaves(data.saves || []); } catch { /* ignore */ }
  };

  const savesPanel = (
    <MySaves bare saves={saves} onCloseView={() => setDraft(null)} onDelete={deleteSave} onRename={(sv, v) => patchSave(sv.save_id, { name: v })} rowValue={(sv) => sv.spent_estimate != null ? ({ text: fmt(sv.spent_estimate), over: budget != null && sv.spent_estimate > budget }) : null} lang={lang} t={t} testid="table-saves" itemLabel={t("saves.items_label")} renderItems={(orig) => { const s = viewOf(orig); return (
    <div>
      <p className="mb-3 text-xs capitalize text-xmas-champagne">{(items.find((x) => x.id === s.style)?.name) || s.style} · {s.guests} {t("ceia.guests")}</p>
      {(s.slots || []).length > 0 && (
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-xmas-champagne">{t("table.cost_title")}</p>
            {s.spent_estimate != null && <b className={`font-mono-num text-sm ${budget != null && s.spent_estimate > budget ? "text-xmas-crimson" : "text-xmas-gold"}`} data-testid="table-saved-total">{fmt(s.spent_estimate)}</b>}
          </div>
          <div className="space-y-2">
            {s.slots.map((sl, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg bg-xmas-navy/40 px-3 py-2" data-testid={`table-saved-slot-${sl.id || i}`}>
                {sl.product?.thumbnail && <img src={sl.product.thumbnail} alt="" className="h-12 w-12 shrink-0 rounded-md bg-white object-contain" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-xmas-champagne">
                    <span>{sl.label}</span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-xmas-gold/25 px-1.5 py-0.5 normal-case tracking-normal">
                      <button type="button" onClick={() => setSavedQty(s, i, Math.max(1, (sl.quantity || 1) - 1))} disabled={(sl.quantity || 1) <= 1} data-testid={`table-saved-qty-minus-${sl.id || i}`} className="rounded-full p-0.5 text-xmas-snow hover:bg-xmas-gold/20 disabled:opacity-30"><Minus className="h-3 w-3" /></button>
                      <span className="font-mono-num min-w-[1.25rem] text-center text-xmas-snow" data-testid={`table-saved-qty-${sl.id || i}`}>{sl.quantity}</span>
                      <button type="button" onClick={() => setSavedQty(s, i, (sl.quantity || 1) + 1)} data-testid={`table-saved-qty-plus-${sl.id || i}`} className="rounded-full p-0.5 text-xmas-snow hover:bg-xmas-gold/20"><Plus className="h-3 w-3" /></button>
                    </span>
                  </div>
                  <p className="line-clamp-1 text-sm text-xmas-snow">{sl.product?.title || "—"}</p>
                  <div className="flex flex-wrap items-center gap-2 text-[11px]">
                    {sl.product?.price && <span className="font-mono-num text-xmas-gold">{sl.product.price}</span>}
                    {sl.product?.source && <span className="text-xmas-slate">{sl.product.source}</span>}
                    {sl.product?.product_link && <a href={sl.product.product_link} target="_blank" rel="noreferrer" className="text-xmas-gold hover:underline">{t("prod.view")}</a>}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <p className="font-mono-num text-sm text-xmas-champagne">{fmt(sl.line_total)}</p>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => openSavedSlotOptions(s, i)} data-testid={`table-saved-swap-${sl.id || i}`} title={t("deco.see_others")} aria-label={t("deco.see_others")} className="rounded-full p-1 text-xmas-slate transition hover:bg-xmas-gold/20 hover:text-xmas-gold"><Search className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => removeSavedSlot(s, i)} data-testid={`table-saved-slot-remove-${sl.id || i}`} title={t("saves.remove_item")} aria-label={t("saves.remove_item")} className="rounded-full p-1 text-xmas-slate transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {!(s.slots || []).length && (<>
      <p className="mb-2 text-xs uppercase tracking-widest text-xmas-champagne">{t("table.elements_needed")}</p>
      <ul className="space-y-1.5">
        {(s.items || []).map((it, i) => (
          <li key={i} data-testid="table-saved-item" className="flex items-center justify-between gap-2 rounded-lg bg-xmas-navy/40 px-3 py-2 text-sm text-xmas-snow">
            <span className="min-w-0 flex-1">{it}</span>
            <span className="flex shrink-0 items-center gap-1">
              <button type="button" onClick={() => findSavedItem(s, it)} data-testid={`table-saved-find-${i}`} title={t("saves.find_product")} aria-label={t("saves.find_product")} className="rounded-full p-1.5 text-xmas-slate transition hover:bg-xmas-gold/20 hover:text-xmas-gold"><Search className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => removeSavedItem(orig, i)} data-testid={`table-saved-remove-${i}`} title={t("saves.remove_item")} aria-label={t("saves.remove_item")} className="rounded-full p-1.5 text-xmas-slate transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson"><Trash2 className="h-3.5 w-3.5" /></button>
            </span>
          </li>
        ))}
      </ul>
      </>)}
      {draft?.saveId === orig.save_id && <SaveChangesBar t={t} onSave={saveDraft} onDiscard={() => setDraft(null)} testid="table-saved-changes" />}
    </div>
    ); }} />
  );

  return (
    <div>
      <PageHeader title={t("table.title")} testid="table-header" />
      <PageTutorial id="table" content={TABLE_TUTORIAL} />
      <CategoryBudget category="table" aside={savesPanel} savedSum={Math.round(saves.reduce((a, sv) => a + Number(sv.spent_estimate || 0), 0) * 100) / 100} />
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {items.map((tb) => (
          <button key={tb.id} onClick={() => { setActive(tb); setGuide(null); }} data-testid={`table-style-${tb.id}`}
            className="card-lift group relative overflow-hidden rounded-2xl border border-xmas-gold/15 text-left">
            <div className="relative h-60">
              <img src={tb.img} srcSet={imgSrcSet(tb.img)} sizes="(max-width: 640px) 100vw, 400px" alt={tb.name} loading="lazy" decoding="async" className="h-full w-full object-cover transition duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-xmas-navy via-xmas-navy/25 to-transparent" />
            </div>
            <div className="absolute inset-x-0 bottom-0 p-5">
              <h3 className="font-display text-xl font-semibold text-xmas-snow">{tb.name}</h3>
              <p className="mt-1 text-sm text-xmas-snow/75">{tb.desc}</p>
              <span className="btn-gold mt-3 inline-block rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider">{t("table.view")}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Detail modal */}
      <Modal open={!!active} onClose={() => setActive(null)} title={active?.name} testid="table-detail-modal">
        {active && (
          <div data-testid={`table-detail-${active.id}`}>
            <img src={active.img} srcSet={imgSrcSet(active.img)} sizes="(max-width: 640px) 100vw, 640px" alt={active.name} decoding="async" className="h-48 w-full rounded-xl object-cover" />
            <p className="mt-3 text-sm text-xmas-slate">{active.desc}</p>

            <h3 className="mt-5 font-display text-lg text-xmas-gold">{t("table.how_to")}</h3>
            <p className="text-xs uppercase tracking-widest text-xmas-champagne">{t("table.steps_title")}</p>
            <ol className="mt-2 space-y-2">
              {(guide?.steps || []).map((s, i) => (
                <li key={i} className="flex gap-3 text-sm text-xmas-snow">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-xmas-gold/20 font-mono-num text-xs text-xmas-gold">{i + 1}</span>{s}
                </li>
              ))}
            </ol>

            <div className="mt-5 flex items-center gap-2">
              <Users className="h-4 w-4 text-xmas-gold" />
              <span className="text-sm text-xmas-snow">{t("table.guests")}:</span>
              <select value={guests} onChange={(e) => setGuests(Number(e.target.value))} data-testid="table-guests-select"
                className="rounded-lg border border-xmas-gold/20 bg-xmas-navy/60 px-2 py-1 text-sm text-xmas-snow outline-none focus:border-xmas-gold">
                {guestOptions.map((g) => <option key={g} value={g} className="bg-xmas-navycard">{g}</option>)}
              </select>
            </div>

            {budget ? (
              <div className="mt-5 rounded-2xl border border-xmas-gold/25 bg-xmas-gold/10 p-4" data-testid="table-cost-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Coins className="h-5 w-5 text-xmas-gold" />
                    <h3 className="font-display text-base text-xmas-snow">{t("table.cost_title")}</h3>
                  </div>
                  {planLoading ? <Loader2 className="h-5 w-5 animate-spin text-xmas-gold" /> : plan?.state === "ok" && (
                    <div className="text-right">
                      <p className={`font-mono-num text-xl font-bold ${plan.spent_estimate > budget ? "text-xmas-crimson" : "text-xmas-gold"}`} data-testid="table-cost-total">{fmt(plan.spent_estimate)}</p>
                      <p className="text-xs text-xmas-slate">{fmt(budget)} · {t("table.for_guests", { n: guests })}</p>
                    </div>
                  )}
                </div>
                {plan?.state === "ok" && plan.spent_estimate > budget && (
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-xmas-crimson" data-testid="table-cost-over"><AlertTriangle className="h-3.5 w-3.5" /> {t("table.cost_over").replace("{s}", fmt(plan.spent_estimate)).replace("{v}", fmt(plan.spent_estimate - budget)).replace("{b}", fmt(budget))}</p>
                )}
                {plan?.state === "not_configured" && <p className="mt-2 text-xs text-xmas-slate">{t("prod.not_configured")}</p>}
                {plan?.state === "ok" && (
                  <div className="mt-3 space-y-2">
                    {plan.slots.map((sl) => (
                      <div key={sl.id} className="flex items-center gap-3 rounded-lg bg-xmas-navy/40 px-3 py-2" data-testid={`table-cost-slot-${sl.id}`}>
                        {sl.product?.thumbnail && <img src={sl.product.thumbnail} alt="" className="h-10 w-10 shrink-0 rounded-md bg-white object-contain" />}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-xmas-champagne">
                            <span>{sl.label}</span>
                            <span className="inline-flex items-center gap-1 rounded-full border border-xmas-gold/25 px-1.5 py-0.5 normal-case tracking-normal">
                              <button type="button" onClick={() => setSlotQty(sl.id, Math.max(1, sl.quantity - 1))} disabled={sl.quantity <= 1} data-testid={`table-slot-qty-minus-${sl.id}`} className="rounded-full p-0.5 text-xmas-snow hover:bg-xmas-gold/20 disabled:opacity-30"><Minus className="h-3 w-3" /></button>
                              <span className="font-mono-num min-w-[1.25rem] text-center text-xmas-snow" data-testid={`table-slot-qty-${sl.id}`}>{sl.quantity}</span>
                              <button type="button" onClick={() => setSlotQty(sl.id, sl.quantity + 1)} data-testid={`table-slot-qty-plus-${sl.id}`} className="rounded-full p-0.5 text-xmas-snow hover:bg-xmas-gold/20"><Plus className="h-3 w-3" /></button>
                            </span>
                          </div>
                          <p className="line-clamp-1 text-sm text-xmas-snow">{sl.product?.title || "—"}</p>
                          {sl.product?.source && <p className="text-[11px] text-xmas-slate">{sl.product.source}</p>}
                        </div>
                        <div className="shrink-0 text-right">
                          <p className={`font-mono-num text-sm ${sl.line_total > sl.allocated ? "text-xmas-crimson" : "text-xmas-gold"}`}>{fmt(sl.line_total)}</p>
                          <div className="flex items-center justify-end gap-2">
                            {sl.product?.product_link && <a href={sl.product.product_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-xmas-gold hover:underline">{t("prod.view")} <ExternalLink className="h-3 w-3" /></a>}
                            <button type="button" onClick={() => openSlotOptions(sl)} data-testid={`table-slot-others-${sl.id}`} className="inline-flex items-center gap-1 rounded-full border border-xmas-gold/30 px-2 py-0.5 text-[11px] text-xmas-snow hover:border-xmas-gold"><Search className="h-3 w-3" /> {t("deco.see_others")}</button>
                            <button type="button" onClick={() => removeSlot(sl.id)} data-testid={`table-slot-remove-${sl.id}`} title={t("saves.remove_item")} aria-label={t("saves.remove_item")} className="rounded-full p-1 text-xmas-slate transition hover:bg-xmas-crimson/20 hover:text-xmas-crimson"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <p className="text-[11px] text-xmas-slate">{t("table.cost_note").replace("{m}", country)}</p>
                    <div className="pt-2">
                      <ProductSearchBar historyKey="table" testid="table-search" onSearch={openCustomSearch} maxLabel={plan.remaining_estimate > 0 ? fmt(plan.remaining_estimate) : null} />
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            <p className="mt-4 text-xs uppercase tracking-widest text-xmas-champagne">{t("table.elements_needed")} · {t("table.for_guests", { n: guests })}</p>
            <p className="mt-1 text-xs text-xmas-slate">{t("table.select_hint")}</p>
            <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {buildItems(active, guide).map((it) => {
                const isSel = selectedEls.includes(it.key);
                return (
                  <button key={it.key} onClick={() => toggleEl(it.key)} data-testid={`table-el-${it.key}`}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition ${isSel ? "border-xmas-gold bg-xmas-gold/10 text-xmas-snow" : "border-xmas-gold/15 text-xmas-slate hover:border-xmas-gold/40"}`}>
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${isSel ? "border-xmas-gold bg-xmas-gold" : "border-xmas-gold/40"}`}>
                      {isSel && <Check className="h-3 w-3 text-xmas-navy" />}
                    </span>
                    {it.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={() => findItems(active)} data-testid="table-find-items" className="btn-gold inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider">
                <Search className="h-4 w-4" /> {t("table.find_items")}
              </button>
              <button onClick={saveTable} data-testid="table-save" className="inline-flex items-center gap-2 rounded-full border border-xmas-gold/30 px-5 py-2.5 text-xs text-xmas-snow hover:border-xmas-gold">
                <Clock className="h-4 w-4" /> {t("saves.save")}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Products modal (after saves so it stacks on top) */}
      <Modal open={!!prodModal} onClose={() => setProdModal(null)} title={prodModal?.label} testid="table-products-modal">
        <ProductResults loading={prodModal?.loading} result={prodModal?.result} onChoose={prodModal?.slotId || prodModal?.saved || prodModal?.custom ? chooseSlotProduct : undefined} />
      </Modal>
    </div>
  );
}
